import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CoverImage } from './CoverImage';
import { cn } from '@/lib/utils';
import { type CoverSize } from '@/lib/coverUtils';

interface BookRowProps {
  bookId: string;
  title: string;
  author?: string;
  isbn13?: string;
  isbn10?: string;
  olid?: string;
  coverUrl?: string;
  coverCacheUrl?: string;
  href?: string;
  right?: React.ReactNode;
  size?: CoverSize;
  className?: string;
}

export function BookRow({
  bookId,
  title,
  author,
  isbn13,
  isbn10,
  olid,
  coverUrl,
  coverCacheUrl,
  href,
  right,
  size = 'md',
  className
}: BookRowProps) {
  const navigate = useNavigate();
  const bookHref = href ?? `/book/${bookId}`;
  const altText = `${title}${author ? ` by ${author}` : ''}`;

  // Get width class based on size
  const sizeWidthClass = {
    xs: 'w-12', // 48px
    sm: 'w-16', // 64px  
    md: 'w-24', // 96px
    lg: 'w-30'  // 120px
  }[size];

  return (
    <div className="border-t border-border/50 mt-2 pt-2 first:border-t-0 first:mt-0 first:pt-0">
      <div
        className={cn(
          "grid items-start gap-4 p-4 rounded-xl bg-white/80",
          "supports-[backdrop-filter]:bg-white/60",
          "grid-cols-[auto,1fr]",
          "md:grid-cols-[auto,minmax(220px,1fr),2fr]",
          className
        )}
      >
        {/* LEFT — Cover */}
        <Link 
          to={bookHref} 
          aria-label={`Open ${altText}`} 
          className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
        >
          <div className={cn(
            "relative aspect-[2/3] overflow-hidden rounded-md shadow-sm flex-shrink-0",
            sizeWidthClass
          )}>
            <CoverImage
              bookId={bookId}
              isbn13={isbn13}
              isbn10={isbn10}
              olid={olid}
              title={title}
              author={author}
              coverUrl={coverUrl}
              coverCacheUrl={coverCacheUrl}
              size={size}
              loading="lazy"
            />
          </div>
        </Link>

        {/* MIDDLE — Meta */}
        <div className="min-w-0">
          <Link 
            to={bookHref} 
            className="group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
          >
            <h3 className={cn(
              "font-semibold leading-snug line-clamp-2 group-hover:text-primary transition-colors",
              size === 'sm' ? 'text-sm' : 'text-base sm:text-lg'
            )}>
              {title}
            </h3>
          </Link>
          {author && (
            <a 
              href={`/author/${encodeURIComponent(author)}`}
              className={cn(
                "text-muted-foreground leading-snug mt-1 hover:text-primary hover:underline transition-colors",
                size === 'sm' ? 'text-xs' : 'text-sm'
              )}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(`/author/${encodeURIComponent(author)}`);
              }}
            >
              {author}
            </a>
          )}
        </div>

        {/* RIGHT — Pluggable content */}
        {right && (
          <div className="min-w-0 flex flex-col gap-2">
            {right}
          </div>
        )}
      </div>
    </div>
  );
}

export default BookRow;